/**
 * Validação de toda entrada da API (Zod). A tela usa os mesmos esquemas, então a mensagem de erro é a mesma dos dois lados.
 */
import { z } from 'zod';
import {
  COST_CATEGORIES, CRITICALITIES, DELIVERY_STATUSES, HEALTHS, INSTALL_MODES, ITEM_KINDS, ITEM_STATUSES, MEETING_KINDS, MEETING_POINT_KINDS, MEMBER_ROLES, PHASES,
  TASK_KINDS, TASK_STATUSES, TERM_KINDS, TERM_STATUSES, TRAVEL_STATUSES, UF_CODES, USER_UPDATE_KINDS,
} from './domain.js';
import { FAST_COLUMN_KEYS } from './fast.js';
import { PriorityInput } from './priority.js';

export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const optDate = IsoDateSchema.nullable().optional();
const money = z.number().finite().min(0).max(1e13);
const optMoney = money.nullable().optional();
const text = (max: number) => z.string().trim().max(max);
const optText = (max: number) => text(max).nullable().optional();
const id = z.number().int().positive();
const optId = id.nullable().optional();
const url = z.string().trim().max(1000).refine((s) => /^https:\/\//i.test(s) || s === '', 'Use um endereço https://');
const email = z.string().trim().toLowerCase().max(320).email('E-mail inválido');

export const ProjectBase = z.object({
  opNumber: z.number().int().positive().max(9_999_999).nullable().optional(),
  name: text(250).min(3, 'Informe o nome do projeto'),
  clientId: id,
  categoryId: optId,
  classificationId: optId,
  phase: z.enum(PHASES).optional(),
  tapNumber: optText(20),
  /** O que é esta versão da TAP (ex.: 400.1 · segunda entrega do canal). */
  tapDescription: optText(300),
  tapDate: optDate,
  tapUrl: url.nullable().optional(),
  kickoffDate: optDate,
  plannedStart: optDate,
  plannedEnd: optDate,
  forecastEnd: optDate,
  completedDate: optDate,
  tePartialDate: optDate,
  teDate: optDate,
  asBuiltDate: optDate,
  /** Desde quando o projeto está em hold (preenchida ao entrar em hold; editável para os que já estavam). */
  holdSince: optDate,
  estimatedDays: z.number().int().min(0).max(5000).nullable().optional(),
  pmUserId: optId,
  techLeadUserId: optId,
  architectUserId: optId,
  sellerUserId: optId,
  sellerName: optText(200),
  sponsorName: optText(200),
  sponsorEmail: email.nullable().optional().or(z.literal('').transform(() => null)),
  sponsorPhone: z.string().trim().max(40).regex(/^[0-9+()\-.\s]*$/, 'Telefone: só números, espaço, +, ( ) e -').nullable().optional().transform((v) => v || null),
  scope: optText(20000),
  /** Estado (UF) onde o projeto é instalado e o modo de instalação: os feriados do cronograma saem daqui. */
  installUf: z.enum(UF_CODES, { message: 'Escolha o estado (UF) de instalação' }).nullable().optional(),
  installMode: z.enum(INSTALL_MODES, { message: 'Escolha o modo de instalação' }).nullable().optional(),
  currencySale: z.enum(['USD', 'BRL', 'EUR']).optional(),
  saleUsd: optMoney,
  saleBrl: optMoney,
  budgetBrl: optMoney,
  budgetHours: z.number().min(0).max(1e6).nullable().optional(),
  invoicedBrl: optMoney,
  invoicedUsd: optMoney,
  billingPending: z.enum(['sim', 'nao', 'a_confirmar']).nullable().optional(),
  billingPendingBrl: optMoney,
  /** Entrega (única ou por marcos) ou contrato recorrente (mensal); vazio: pelo nome do projeto ("(3yrs)", "(18m)"). */
  billingMode: z.enum(['entrega', 'recorrente']).nullable().optional(),
  contractMonths: z.number().int().min(1, 'Prazo de 1 a 240 meses').max(240, 'Prazo de 1 a 240 meses').nullable().optional(),
  billingStart: optDate,
  actualCostBrl: optMoney,
  /** Um ou mais pedidos de venda (SO1198, ou SO632, SO633). */
  netsuiteSoNumber: text(200).regex(/^[A-Za-z0-9#\-_.,;/ ]*$/, 'Pedido de venda: letras e números, separados por vírgula (ex.: SO632, SO633)').nullable().optional(),
  /** Parte do projeto num pedido de venda dividido com outros projetos (%; vazio: partes iguais). */
  netsuiteSharePct: z.number().gt(0, 'A parte no pedido é maior que 0%').max(100, 'A parte no pedido vai até 100%').nullable().optional(),
  /** ID interno do projeto (Job) no NetSuite, para a integração "Pelo projeto (Job)". */
  netsuiteProjectId: z.string().trim().regex(/^\d*$/, 'Só números (ID interno do Job)').max(40).nullable().optional(),
  hasSupport: z.boolean().optional(),
  /** Vigência do suporte em meses (com "Tem suporte? Sim"; sem suporte, fica vazia). */
  supportMonths: z.number().int('Vigência em meses inteiros').min(1, 'Vigência de 1 a 240 meses').max(240, 'Vigência de 1 a 240 meses').nullable().optional(),
  progressMode: z.enum(['manual', 'cronograma']).optional(),
  progressPct: z.number().min(0).max(100).optional(),
  priority: PriorityInput.nullable().optional(),
  vendorIds: z.array(id).max(20).optional(),
  supplierIds: z.array(id).max(20).optional(),
});
export const ProjectCreate = ProjectBase;
export const ProjectPatch = ProjectBase.partial().extend({ rowVersion: z.number().int().positive() });

/** A saúde muda sem justificativa obrigatória; a observação, se houver, vai para o diário (o contexto fica nas reuniões e no diário). */
export const HealthChange = z.object({ health: z.enum(HEALTHS), reason: optText(500) });
/** Mudar a fase: o motivo é opcional (muitas vezes já está num registro do diário, como o da reunião). */
export const PhaseChange = z.object({ phase: z.enum(PHASES), reason: optText(500) });
/** Nova versão da TAP (400 → 400.1…), para uma nova entrega no mesmo projeto. */
export const TapVersionInput = z.object({
  description: text(300).min(5, 'Descreva em poucas palavras o que é esta versão (mínimo 5 caracteres)'),
  date: IsoDateSchema.optional(),
  url: optText(1000),
  /** Projeto finalizado ou em encerramento: volta para Execução para a nova entrega. */
  reopen: z.boolean().default(false),
});
/** Encerrar sem TE: justificativa obrigatória (fica no projeto, no histórico, no diário e na auditoria). */
export const CloseWithoutTe = z.object({ reason: text(1000).min(20, 'Explique por que o projeto é encerrado sem o TE (mínimo 20 caracteres)') });
export const BaselineSet = z.object({ reason: text(500).min(5, 'Explique o motivo (mínimo 5 caracteres)') });

export const MemberInput = z.object({ userId: id, role: z.enum(MEMBER_ROLES), allocationPct: z.number().min(0).max(100).nullable().optional() });

export const UpdateCreate = z.object({
  eventDate: IsoDateSchema,
  kind: z.enum(USER_UPDATE_KINDS as [string, ...string[]]).default('atualizacao'),
  body: text(20000).min(3, 'Escreva o registro'),
  health: z.enum(HEALTHS).nullable().optional(),
  healthReason: optText(500),
  progressPct: z.number().min(0).max(100).nullable().optional(),
  aiAssisted: z.boolean().optional(),
  correctsUpdateId: z.number().int().positive().nullable().optional(),
  meetingId: optId,
});
export const RedactInput = z.object({ reason: text(500).min(5, 'Explique o motivo (mínimo 5 caracteres)') });

export const TaskInput = z.object({
  parentId: z.number().int().positive().nullable().optional(),
  name: text(250).min(2, 'Informe o nome da atividade'),
  kind: z.enum(TASK_KINDS).default('tarefa'),
  plannedStart: optDate,
  plannedEnd: optDate,
  actualStart: optDate,
  actualEnd: optDate,
  estimatedHours: z.number().min(0).max(100000).nullable().optional(),
  weight: z.number().min(0).max(100000).nullable().optional(),
  percent: z.number().min(0).max(100).optional(),
  status: z.enum(TASK_STATUSES).optional(),
  assigneeUserId: optId,
  notes: optText(5000),
  dependsOn: z.array(z.object({ taskId: z.number().int().positive(), type: z.enum(['FS', 'SS', 'FF', 'SF']).default('FS'), lagDays: z.number().int().min(-365).max(365).default(0) })).max(30).optional(),
});
export const TaskPatch = TaskInput.partial().extend({ rowVersion: z.number().int().positive() });
export const TaskReorder = z.object({ order: z.array(z.object({ id: z.number().int().positive(), parentId: z.number().int().positive().nullable(), sortOrder: z.number().int() })).max(2000) });
export const ApplyTemplate = z.object({ templateId: id, startDate: IsoDateSchema });

const TimeEntryBase = z.object({
  /** A hora é de um projeto (projectId, com atividade do cronograma opcional) ou de uma atividade interna (activityId). */
  projectId: optId,
  taskId: z.number().int().positive().nullable().optional(),
  /** Atividade de projeto fora do cronograma (kickoff, reunião com o cliente…), no lugar da atividade do cronograma. */
  projectActivityId: optId,
  activityId: optId,
  /** Área da reunião (quando a atividade interna pede a área). */
  areaId: optId,
  workDate: IsoDateSchema,
  minutes: z.number().int().min(1, 'Informe as horas').max(1440),
  billable: z.boolean().default(true),
  description: optText(1000),
  /** Ao apontar, a pessoa pode informar o novo percentual da atividade: o cronograma e o projeto avançam juntos. */
  taskPercent: z.number().min(0).max(100).nullable().optional(),
});
const oneTarget = (e: { projectId?: number | null; activityId?: number | null }) => !!e.projectId !== !!e.activityId;
export const TimeEntryInput = TimeEntryBase.refine(oneTarget, { message: 'Escolha um projeto ou uma atividade interna.', path: ['projectId'] });
export const TimeEntryPatch = TimeEntryBase.partial().extend({ rowVersion: z.number().int().positive() });
/**
 * Lançamentos trazidos da agenda do Outlook (a pessoa revisa e confirma na tela): cada um com a referência do evento
 * (hash), para o mesmo evento não entrar duas vezes. Entram como rascunho.
 */
export const TimeImportInput = z.object({
  entries: z.array(TimeEntryBase.omit({ taskPercent: true }).extend({ sourceRef: z.string().regex(/^outlook:[0-9a-f]{40}$/) })
    .refine(oneTarget, { message: 'Escolha um projeto ou uma atividade interna.', path: ['projectId'] })).min(1).max(100),
});
export const TimeSubmit = z.object({ weekStart: IsoDateSchema });
export const TimeDecision = z.object({ ids: z.array(z.number().int().positive()).min(1).max(500), decision: z.enum(['aprovar', 'reprovar', 'reabrir']), note: optText(500) });
/** Atividade interna nova, criada no próprio apontamento (permissão time.activities). */
export const InternalActivityInput = z.object({ name: text(80).min(2, 'Dê um nome à atividade'), asksArea: z.boolean().default(false) });

export const ItemInput = z.object({
  kind: z.enum(ITEM_KINDS).default('acao'),
  title: text(300).min(3, 'Descreva o item'),
  description: optText(10000),
  context: optText(200),
  criticality: z.enum(CRITICALITIES).default('media'),
  probability: z.number().int().min(1).max(5).nullable().optional(),
  impact: z.number().int().min(1).max(5).nullable().optional(),
  ownerUserId: optId,
  ownerName: optText(200),
  dueDate: optDate,
  status: z.enum(ITEM_STATUSES).default('pendente'),
  resolution: optText(10000),
});
export const ItemPatch = ItemInput.partial().extend({ rowVersion: z.number().int().positive() });

export const TermInput = z.object({
  kind: z.enum(TERM_KINDS),
  number: optText(40),
  title: optText(250),
  issuedDate: optDate,
  signedDate: optDate,
  url: url.nullable().optional(),
  status: z.enum(TERM_STATUSES).default('pendente'),
  signerName: optText(200),
  signerEmail: email.nullable().optional().or(z.literal('').transform(() => null)),
  notes: optText(1000),
});
export const TermPatch = TermInput.partial();
export const TermStepPatch = z.object({ doneOn: optDate, note: optText(500) });

/** Uma linha dos Canais FAST, com as colunas da aba da planilha. Lançamento aceita data ou texto (ex.: "Cancelado"). */
export const FastRowInput = z.object({
  mark: optText(10),
  clientLabel: optText(80),
  contentPartner: optText(160),
  kind: z.enum(['canal', 'endpoint']).default('canal'),
  channelName: text(200).min(1, 'Informe o nome do canal'),
  platform: text(120).min(1, 'Informe a plataforma'),
  region: optText(40),
  deliveryFormat: optText(80),
  codec: optText(60),
  launchDate: optDate,
  launchText: optText(40),
  forecastDate: optDate,
  statusText: optText(200),
  progressPct: z.number().min(0).max(100).nullable().optional(),
  opNumber: z.number().int().positive().max(9_999_999).nullable().optional(),
  description: optText(300),
  tapRef: optText(20),
});
export const FastRowPatch = FastRowInput.partial().extend({ rowVersion: z.number().int().positive() });

/** Link externo de leitura: colunas que aparecem, filtros fixos, validade (até 90 dias) e se permite baixar o Excel. */
export const ShareLinkInput = z.object({
  title: text(160).min(3, 'Dê um título ao link'),
  audience: optText(160),
  days: z.number().int().min(1).max(90),
  columns: z.array(z.enum(FAST_COLUMN_KEYS)).min(1, 'Escolha ao menos uma coluna'),
  filters: z.object({
    clients: z.array(text(80)).max(200).optional(),
    contentPartners: z.array(text(160)).max(200).optional(),
    platforms: z.array(text(120)).max(200).optional(),
    categories: z.array(z.enum(DELIVERY_STATUSES)).max(5).optional(),
  }).default({}),
  allowDownload: z.boolean().default(false),
});

/** Documento do projeto: o tipo vem do cadastro Tipos de documento (Administração › Cadastros). */
export const DocumentInput = z.object({ title: text(250).min(2), typeId: z.number({ message: 'Escolha o tipo' }).int().positive(), url: url.refine((s) => s.length > 0, 'Informe o endereço') });

const currency = z.enum(['BRL', 'USD', 'EUR']).default('BRL');
export const BudgetLineInput = z.object({ category: z.enum(COST_CATEGORIES), description: text(300).min(2), amount: z.number().finite().max(1e13), currency, fxRate: z.number().positive().max(1000).default(1) });
export const CostEntryInput = z.object({ category: z.enum(COST_CATEGORIES), entryDate: IsoDateSchema, description: text(300).min(2), amount: z.number().finite().max(1e13), currency, fxRate: z.number().positive().max(1000).default(1), document: optText(80), partnerId: optId });
export const BillingInput = z.object({ invoiceNumber: optText(60), issueDate: optDate, dueDate: optDate, description: optText(300), amount: z.number().finite().max(1e13), currency, fxRate: z.number().positive().max(1000).default(1), status: z.enum(['previsto', 'emitido', 'pago', 'cancelado']).default('previsto') });

export const TravelInput = z.object({
  travelerUserId: id, destination: text(200).min(2), purpose: text(500).min(3), departDate: IsoDateSchema, returnDate: IsoDateSchema,
  estimatedCost: optMoney, notes: optText(1000),
});
export const TravelPatch = z.object({ status: z.enum(TRAVEL_STATUSES).optional(), actualCost: optMoney, estimatedCost: optMoney, notes: optText(1000) });

export const AllocationInput = z.object({ projectId: id, userId: id, startDate: IsoDateSchema, endDate: IsoDateSchema, hoursPerWeek: z.number().min(0).max(80), notes: optText(300) });
export const AbsenceInput = z.object({ userId: id, startDate: IsoDateSchema, endDate: IsoDateSchema, kind: z.enum(['ferias', 'folga', 'licenca', 'treinamento', 'outro']), notes: optText(300) });
export const RateInput = z.object({ validFrom: IsoDateSchema, hourlyCost: money, notes: optText(300) });

export const MeetingInput = z.object({ title: text(200).min(3), meetingDate: IsoDateSchema, kind: z.enum(MEETING_KINDS).default('semanal'), attendeeIds: z.array(id).max(100).default([]), notes: optText(50000) });
export const MeetingPointInput = z.object({
  kind: z.enum(MEETING_POINT_KINDS).default('informacao'),
  body: text(5000).min(3, 'Escreva o ponto tratado'),
  projectId: optId,
  ownerUserId: optId,
  dueDate: optDate,
});
export const MeetingReview = z.object({
  projectId: id,
  body: optText(20000),
  health: z.enum(HEALTHS).nullable().optional(),
  healthReason: optText(500),
  progressPct: z.number().min(0).max(100).nullable().optional(),
});

export const ProjectListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.string().max(200).optional(),
  phase: z.string().max(200).optional(),
  pm: z.string().max(200).optional(),
  tech: z.string().max(200).optional(),
  client: z.string().max(200).optional(),
  category: z.string().max(200).optional(),
  classification: z.string().max(200).optional(),
  vendor: z.string().max(200).optional(),
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  active: z.enum(['1', '0', 'all']).optional(),
  mine: z.enum(['1']).optional(),
  /** Projetos de que a pessoa participa (gerente, engenheiro de campo, vendedor ou equipe). */
  person: z.coerce.number().int().positive().optional(),
  archived: z.enum(['1']).optional(),
  sort: z.string().max(40).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type ProjectListQuery = z.infer<typeof ProjectListQuery>;

export type ProjectCreate = z.infer<typeof ProjectCreate>;
export type ProjectPatch = z.infer<typeof ProjectPatch>;
export type TaskInput = z.infer<typeof TaskInput>;
export type TimeEntryInput = z.infer<typeof TimeEntryInput>;
export type TimeImportInput = z.infer<typeof TimeImportInput>;
export type ItemInput = z.infer<typeof ItemInput>;
